<template>
  <div v-if="isMember && memberPrice">
    <div class="AsidePrices-price AsidePrices-price--memberLoggedIn">
      <ai-amount
        main-typo="paragraph-01-bold"
        decimal-typo="paragraph-03-bold"
        :amount="convertedMemberPrice.amount"
        :symbol="convertedMemberPrice.currency?.code"
        member-tag="left"
        member-tag-mobile-inverse
        member-tag-top-offset="8%" />
    </div>
    <div
      v-if="convertedPublicPrice.amount"
      class="AsidePrices-price AsidePrices-price--publicLoggedIn">
      <ai-amount
        :amount="convertedPublicPrice.amount"
        main-typo="paragraph-03-regular"
        decimal-typo="paragraph-03-regular"
        :symbol="convertedPublicPrice.currency?.code" />
    </div>
  </div>
  <div v-else-if="isMember && !memberPrice">
    <div class="AsidePrices-price AsidePrices-price--memberLoggedIn">
      <ai-amount
        main-typo="paragraph-01-bold"
        decimal-typo="paragraph-03-bold"
        :amount="convertedPublicPrice.amount"
        :symbol="convertedPublicPrice.currency?.code" />
    </div>
  </div>
  <div v-else class="AsidePrices-table" role="table">
    <div class="AsidePrices-price AsidePrices-price--public" role="row">
      <ai-typo
        v-if="memberPrice"
        as="span"
        variant="overline-01"
        class="AsidePrices-rates"
        role="cell">
        {{ $t('booking.roomRate.publicLabel') }}
      </ai-typo>
      <ai-amount
        main-typo="paragraph-01-regular"
        decimal-typo="paragraph-03-regular"
        :amount="convertedPublicPrice.amount"
        :symbol="convertedPublicPrice.currency?.code"
        role="cell" />
    </div>
    <div
      v-if="memberPrice"
      class="AsidePrices-price AsidePrices-price--member"
      role="row">
      <ai-typo
        as="span"
        variant="overline-01"
        class="AsidePrices-rates"
        role="cell">
        {{ $t('booking.roomRate.memberLabel') }}
      </ai-typo>
      <ai-amount
        main-typo="paragraph-01-bold"
        decimal-typo="paragraph-03-bold"
        :amount="convertedMemberPrice.amount"
        :symbol="convertedMemberPrice.currency?.code"
        role="cell" />
    </div>
  </div>
</template>

<script setup lang="ts">
type Props = {
  currency?: string | null;
  isMember: boolean;
  memberPrice?: number | null;
  publicPrice: number | null;
  userCurrency?: string;
};

const props = withDefaults(defineProps<Props>(), {
  currency: null,
  memberPrice: null,
  publicPrice: null,
  userCurrency: undefined,
});

const hotelCurrency = computed(() => props.currency);
const targetCurrency = computed(() => props.userCurrency);

const convertedPublicPrice = useConvertToUserCurrency(
  props.publicPrice || 0,
  hotelCurrency,
  targetCurrency,
);

const convertedMemberPrice = useConvertToUserCurrency(
  props.memberPrice || 0,
  hotelCurrency,
  targetCurrency,
);
</script>

<style lang="scss" scoped>
@use '@/assets/styles/utilities/colors';
@use '@/assets/styles/utilities/constants';
@use '@/assets/styles/utilities/mq';

.AsidePrices-table {
  display: flex;
  flex-direction: column;
  align-items: flex-end;
}

.AsidePrices-rates {
  text-transform: uppercase;
  margin-right: constants.$inner-02;
  margin-bottom: constants.$inner-01;

  @media (mq.$from-medium) {
    margin-bottom: constants.$inner-01;
  }
}

.AsidePrices-price {
  position: relative;
  margin-bottom: constants.$margin-01;
  width: max-content;

  &--public {
    margin-bottom: constants.$inner-00;
  }

  &--member {
    margin-bottom: constants.$margin-01;
    background-color: colors.$gold-100;
  }

  &--memberLoggedIn {
    padding-top: 0;
    padding-bottom: 0;
    margin-bottom: 0;
  }

  &--publicLoggedIn {
    padding-top: 0;
    padding-bottom: 0;
    text-decoration: line-through;
    margin-bottom: constants.$margin-01;
  }
}
</style>
